/**
 * Derived character numbers: the level curve, the gear fold, and what a kill is worth.
 *
 * Everything here is pure and side-effect free, and everything here is the **single** place a
 * number is derived. The server calls `computeStats` whenever equipment or level changes and
 * caches the result on `Player`; the client calls the same function for the character sheet, so
 * the two can never disagree about how much health a level-8 Vanguard in a full set has.
 *
 * ## Why this file does not import `ItemDef`
 *
 * `items.ts` is owned by the gear milestone and is being authored concurrently. Coupling the stat
 * fold to whatever shape `ItemDef` lands in would make this file un-landable until that one is.
 * So the input is `GearStatLine` — a structural bag of optional numbers that any item definition
 * with the obvious field names satisfies — and the one server call site adapts. A slot with
 * nothing in it contributes nothing; a character with no gear at all still has working stats,
 * which is what makes a fresh level-1 able to swing at something.
 */

import { MAX_HP } from './constants.js';
import { CallingId } from './callings.js';

// ---------------------------------------------------------------------------------------------
// Inputs
// ---------------------------------------------------------------------------------------------

/**
 * What one equipped piece contributes. Structural on purpose (see the file header): any object
 * with these field names — an `ItemDef`, a test literal, a row read out of SQLite — is accepted.
 *
 * Every field is optional and every absent field is zero, so a piece that only carries armour is
 * written `{ armour: 12 }` and nothing downstream has to care that it is not a weapon.
 */
export interface GearStatLine {
  /** Attack power: scales every `melee` and `charge` coefficient, and the auto-attack. */
  ap?: number;
  /** Spell power: scales every `spell`, `heal`, `hot` and `shield` coefficient. */
  sp?: number;
  /** Flat damage reduction feedstock; see `armorReduction` in `items.ts`. */
  armour?: number;
  /** Flat max-HP bonus (a chest piece, a trinket). */
  maxHp?: number;
  /**
   * Weapon only: the base damage of one swing, before attack power. A character with two of
   * these equipped (which the slot model does not allow) would sum them; the fold does not
   * special-case it because it cannot happen.
   */
  damage?: number;
  /** Weapon only: milliseconds between auto-attack swings. The **slowest** equipped one wins. */
  speedMs?: number;
}

/** Everything the combat code reads off a character. */
export interface DerivedStats {
  maxHp: number;
  /** Attack power. Melee coefficients multiply this. */
  ap: number;
  /** Spell power. Spell, heal, shield and hot coefficients multiply this. */
  sp: number;
  armour: number;
  /** Rage (Vanguard) or mana (Warden) ceiling. 0 for a calling with no resource. */
  resourceMax: number;
  /** Base damage of one auto-attack swing, before attack power. */
  weaponDamage: number;
  /** Milliseconds between auto-attack swings. */
  weaponSpeedMs: number;
  /** How far an auto-attack reaches: melee for a Vanguard, the lantern's 30 m for a Warden. */
  autoAttackRangeM: number;
}

// ---------------------------------------------------------------------------------------------
// The curves
// ---------------------------------------------------------------------------------------------

/**
 * Health at level 1, before the calling multiplier. Kept equal to the old flat `MAX_HP` so a
 * level-1 character has exactly the health the game has always started people with, and so the
 * constant keeps a meaning rather than becoming a stranded 100 nobody dares delete.
 */
export const BASE_HP = MAX_HP;
/** Added per level above 1, before the calling multiplier. Level 10 is ~2.6x level 1. */
export const HP_PER_LEVEL = 18;

/**
 * How much tougher each calling is than the baseline. A tank that is not visibly harder to kill
 * than the healer standing behind it is a tank in name only, so the spread is deliberately wide
 * enough to read in a health bar rather than only in a spreadsheet.
 */
export const CALLING_HP_MULT: Record<number, number> = {
  [CallingId.Vanguard]: 1.3,
  [CallingId.Warden]: 1.0,
  [CallingId.Shade]: 0.9,
  [CallingId.Tinker]: 1.0,
};

/** Attack/spell power at level 1, and the gain per level. */
export const POWER_BASE = 10;
export const POWER_PER_LEVEL = 6;

/** Rage is a fixed 0..100 at every level: it is a rhythm, not a pool that grows. */
export const RAGE_MAX = 100;
/** Mana at level 1, and the gain per level — this one *is* a pool, so it grows. */
export const MANA_BASE = 120;
export const MANA_PER_LEVEL = 20;

/** Default weapon when nothing is equipped, per calling. Damage, swing time, reach. */
const BARE_HANDS: Record<number, { damage: number; speedMs: number; rangeM: number }> = {
  [CallingId.Vanguard]: { damage: 7, speedMs: 2000, rangeM: 5 },
  [CallingId.Warden]: { damage: 5, speedMs: 2400, rangeM: 30 },
  [CallingId.Shade]: { damage: 6, speedMs: 1600, rangeM: 5 },
  [CallingId.Tinker]: { damage: 6, speedMs: 2000, rangeM: 25 },
};
const DEFAULT_WEAPON = { damage: 6, speedMs: 2000, rangeM: 5 };

/** Max HP for a level and calling, before gear. The curve `MAX_HP` used to be a constant of. */
export function maxHpForLevel(level: number, calling = 0): number {
  const l = Math.max(1, Math.floor(level));
  const mult = CALLING_HP_MULT[calling] ?? 1;
  return Math.round((BASE_HP + HP_PER_LEVEL * (l - 1)) * mult);
}

/** Attack or spell power for a level, before gear. Both scale identically; gear separates them. */
export function powerForLevel(level: number): number {
  const l = Math.max(1, Math.floor(level));
  return POWER_BASE + POWER_PER_LEVEL * (l - 1);
}

/** The resource ceiling for a calling at a level: rage is flat, mana grows, others have none. */
export function resourceMaxFor(calling: number, level: number): number {
  if (calling === CallingId.Vanguard) return RAGE_MAX;
  if (calling === CallingId.Warden) {
    return MANA_BASE + MANA_PER_LEVEL * (Math.max(1, Math.floor(level)) - 1);
  }
  return 0;
}

// ---------------------------------------------------------------------------------------------
// The fold
// ---------------------------------------------------------------------------------------------

/**
 * Level + calling + what is worn, folded into the numbers combat reads.
 *
 * `equipped` may contain `undefined` holes (an empty slot in a fixed-length equipment array) and
 * may be empty entirely. Weapon speed takes the **slowest** equipped speed rather than summing or
 * averaging, because a swing timer is a property of the thing being swung and two of them is not
 * a state the slot model can reach — taking the max just means "the weapon, if there is one".
 */
export function computeStats(
  level: number,
  calling: number,
  equipped: readonly (GearStatLine | undefined)[] = [],
): DerivedStats {
  const bare = BARE_HANDS[calling] ?? DEFAULT_WEAPON;
  let gearAp = 0;
  let gearSp = 0;
  let armour = 0;
  let gearHp = 0;
  let weaponDamage = 0;
  let weaponSpeedMs = 0;
  for (const piece of equipped) {
    if (!piece) continue;
    gearAp += piece.ap ?? 0;
    gearSp += piece.sp ?? 0;
    armour += piece.armour ?? 0;
    gearHp += piece.maxHp ?? 0;
    weaponDamage += piece.damage ?? 0;
    if ((piece.speedMs ?? 0) > weaponSpeedMs) weaponSpeedMs = piece.speedMs ?? 0;
  }
  const power = powerForLevel(level);
  return {
    maxHp: Math.max(1, maxHpForLevel(level, calling) + gearHp),
    ap: power + gearAp,
    sp: power + gearSp,
    armour,
    resourceMax: resourceMaxFor(calling, level),
    weaponDamage: weaponDamage > 0 ? weaponDamage : bare.damage,
    weaponSpeedMs: weaponSpeedMs > 0 ? weaponSpeedMs : bare.speedMs,
    autoAttackRangeM: bare.rangeM,
  };
}

/**
 * What an ability with `coefficient` does in the hands of somebody with this much power.
 *
 * One multiply, in one place, so the ability table's coefficients mean the same thing for damage,
 * for healing and for an absorb shield, and so a balance pass can read the table and know what it
 * is looking at. Floored at 1 — an ability that lands and does nothing reads as a bug to a player
 * whatever the arithmetic says.
 */
export function abilityAmount(coefficient: number, power: number): number {
  return Math.max(1, Math.round(coefficient * power));
}

/** One auto-attack swing: the weapon's own damage, scaled by attack power. */
export function autoAttackDamage(stats: DerivedStats): number {
  return Math.max(1, Math.round(stats.weaponDamage * (1 + stats.ap / 100)));
}

// ---------------------------------------------------------------------------------------------
// XP for a kill
// ---------------------------------------------------------------------------------------------

/**
 * How far below you a monster has to be before it is worth nothing. The owner's number.
 *
 * It is also the whole reason a zone has a level *gradient* rather than a level: without a grey
 * band, the safest ground in the Downs is the most profitable, and nobody ever walks north.
 */
export const GREY_LEVEL_GAP = 6;
/** Cap on the bonus for punching above your weight, so a level-1 cannot farm a level-10. */
export const XP_LEVEL_BONUS_CAP = 1.2;

/**
 * XP for killing a monster of `mobLevel` at `playerLevel`.
 *
 * Shaped like the genre's: a flat base that rises with the monster's own level, a modest bonus
 * for fighting up, a linear fade for fighting down, and zero past the grey gap. The fade is
 * linear across exactly `GREY_LEVEL_GAP` levels so the last worthwhile kill still pays a sixth
 * rather than falling off a cliff a player cannot see coming.
 */
export function xpForKill(playerLevel: number, mobLevel: number): number {
  const p = Math.max(1, Math.floor(playerLevel));
  const m = Math.max(1, Math.floor(mobLevel));
  if (m <= p - GREY_LEVEL_GAP) return 0;
  const base = 5 * m + 45;
  if (m >= p) return Math.round(base * Math.min(XP_LEVEL_BONUS_CAP, 1 + 0.05 * (m - p)));
  return Math.max(1, Math.round((base * (GREY_LEVEL_GAP - (p - m))) / GREY_LEVEL_GAP));
}
